import { inspectionCatalogV1EnUS } from "./catalog/v1/en-US";

export const inspectionEnUS = {
  title: "Inspection",
  loading: "Loading…",
  actions: {
    new: "New inspection",
    openForm: "Open form",
    delete: "Delete",
    cancel: "Cancel",
    close: "Close",
    back: "Back",
    next: "Next",
    save: "Save",
    finalize: "Finalize",
    add: "Add",
    filter: "Filter",
    clearFilters: "Clear filters",
    download: "Download",
    downloadReport: "Download",
  },
  kpi: {
    finalized: "Finalized",
  },
  conservationState: {
    title: "Conservation state",
    caption: "Conservation state (current for the structure — ANM classification)",
    sum: "∑EC",
    empty: "Conservation state not filled yet.",
    orient:
      "Select the corresponding weight; if any column has weight 10, proceed with a special inspection.",
    specialWarnBefore:
      "Weight 10 selected in at least one column — proceed with a ",
    specialWarnEmphasis: "special inspection",
    specialWarnAfter: " per ANM rules.",
  },
  chart: {
    title: "Tracking",
    period: "Period",
    monthAbbrev: {
      jan: "jan",
      feb: "feb",
      mar: "mar",
      apr: "apr",
      may: "may",
      jun: "jun",
      jul: "jul",
      aug: "aug",
      sep: "sep",
      oct: "oct",
      nov: "nov",
      dec: "dec",
    },
    periodMonths: {
      three: "Last 3 months",
      six: "Last 6 months",
      twelve: "Last 12 months",
    },
    empty: "No data in this period",
    series: {
      total: "Total",
      finalized: "Finalized",
      notFinalized: "Not finalized",
      scheduled: "Scheduled",
      manual: "Unscheduled",
    },
  },
  table: {
    filterButtonText: "Filters",
    empty: "No inspections registered for this structure.",
    emptyFiltered: "No inspections found with the applied filters.",
    fallback: "—",
    columns: {
      code: "Inspection code",
      responsible: "Inspection responsible",
      date: "Date",
      conservationState: "Conservation state",
      anomalies: "Anomalies",
      status: "Status",
      origin: "Origin",
      pdf: "Inspection sheet",
    },
    pdfAvailableAfterFinalize: "Available after finalization",
    pdfUnavailable: "Inspection sheet not available for download yet",
    pdfPreviewTitle: "Inspection sheet",
    pdfVerificationCode: "Verification code:",
  },
  status: {
    open: "Open",
    inProgress: "in progress",
    finalized: "finalized",
    notFinalized: "Not finalized",
  },
  origin: {
    scheduled: "Scheduled",
    manual: "Unscheduled",
  },
  filters: {
    code: "Inspection code",
    responsible: "Inspection responsible",
    dateFrom: "From",
    dateTo: "To",
    status: "Status",
    origin: "Origin",
    hasAnomalies: "With anomalies",
    all: "All",
    allFeminine: "All",
    yes: "Yes",
    no: "No",
    codePlaceholder: "E.g.: 42",
    responsiblePlaceholder: "Type the name…",
    dateRangeInvalid: "The start date cannot be after the end date",
  },
  form: {
    title: "Inspection sheet",
    titleWithCode: "Inspection sheet #{{code}}",
    unsupportedVersion:
      "This inspection sheet version is not supported by the current app build ({{version}}). Reload the page; if it persists, contact support.",
    stepsHeading: "Form steps",
    stepOf: "Step {{current}} of {{total}}",
    saveAndContinue: "Save and continue",
    removeOccurrence: "Remove",
    selectPlaceholder: "Select…",
    stepLocked: "Complete previous steps to unlock",
    stepIncomplete:
      "Answer the required items of this step to save and continue. Incomplete fields are marked below.",
    fieldErrorAnswer: "Select an option.",
    fieldErrorObservations: "Fill in the observations.",
    fieldErrorPhotos: "Upload at least one photo.",
    fieldErrorValue: "Fill in this field.",
    readOnly: "Read only",
    readOnlyWithStatus: "Read only ({{status}})",
    extrasHint:
      "An extra participant can be removed while they have not signed yet.",
    stepNotApplicable:
      "Step marked as not applicable — items are hidden and excluded from the anomaly count.",
    stepAppliesQuestion: "Does this stage apply to the structure?",
    observationsPlaceholder: "Describe the observed condition…",
    observationsPlaceholderOptional: "Observations (optional)…",
    anomalyReqNote:
      "Answer marked as anomaly — observations and photo are required.",
    anomalyReqNoteGrade:
      "“Regular” and “Deficient” count as anomaly — observations and photo are required.",
    collapseExpand: "Expand",
    collapseMinimize: "Minimize",
    occurrenceEmpty: "“Yes” requires ≥ 1 record. Add at least one.",
    occurrenceEmptyCrack: "“Yes” requires ≥ 1 record. Add a crack.",
    occurrenceEmptySettlement: "“Yes” requires ≥ 1 record. Add a settlement.",
    occurrenceEmptySeepage: "“Yes” requires ≥ 1 record. Add a seepage.",
    occurrenceEmptyErosion: "“Yes” requires ≥ 1 record. Add an erosion.",
    occurrenceEmptyChannel:
      "“Yes” requires ≥ 1 line. Add at least one channel line.",
    occurrenceEmptyGeomembrane:
      "Regular/Deficient requires ≥ 1 geomembrane occurrence.",
    collapseYesPending: "Yes — pending",
    collapseRecordCount: "{{count}} record(s)",
    collapseLineCount: "{{count}} line(s)",
    collapseCleanliness: "Cleanliness",
    collapseStructure: "Structure",
    pendingSummary: "Pending",
    optionalSummary: "Optional",
    occurrencePendingTag: "Pending record",
    entryLineTag: "Line {{n}}",
    drainageChannelDoubleAnomalyHint:
      "Regular/Deficient cleanliness and Regular/Deficient structure count as independent anomalies (+2 on this line).",
    anomalyTag: "anomaly",
    naCheckbox: "N/A (not applicable)",
    entryAnomNote: "Each record counts as 1 anomaly.",
    entryAnomNoteGeomembrane: "Each occurrence (G1, G2…) counts as 1 anomaly.",
    hintOptional:
      "Optional — does not count as an anomaly and does not block finalization.",
    hintInformative:
      "Informative only — does not count as an anomaly and does not require a photo.",
    hintInformativeNoNa:
      "Informative only — does not count as an anomaly. No N/A option.",
    extraSourceLegend: "Add as",
    extraSourceName: "External person",
    extraSourceGs3User: "GS3 user",
    extraSourceHelpAria: "What GS3 user and external person mean",
    extraSourceHelpGs3:
      "GS3 user: already has an account. The name comes from the registration.",
    extraSourceHelpExternal:
      "External person: has no GS3 account. Enter the person’s name.",
    extraDuplicate: "This participant is already on the inspection.",
    gs3User: "GS3 user",
    gs3UserPlaceholder: "Search user…",
    gs3UserNotFound: "No users found",
    participantNamePlaceholder: "Person’s name",
    generalData: {
      code: "Inspection code",
      responsible: "Inspection responsible",
      date: "Inspection date",
      dateScheduledSuffix: "(scheduled — cannot be changed)",
      extraParticipants: "Extra participants",
      addExtraParticipant: "Add",
      extraParticipantName: "Name",
    },
    generalComments: {
      label: "General comments",
      placeholder:
        "Detail all points observed in the inspection, including maintenance, works or interventions on the structure…",
    },
  },
  photos: {
    remove: "Remove",
    expand: "Enlarge {{name}}",
    preview: "Photo {{name}}",
    closePreview: "Close photo",
    previous: "Previous photo",
    next: "Next photo",
    counter: "{{current}} of {{total}}",
    save: "Save",
    deleteTitle: "Remove photo",
    deleteCaption: "Photo to be removed:",
    deleteDescription:
      "The photo will disappear from the form. You can attach it again if needed.",
    deleteConfirm: "Remove",
    deleteCancel: "Cancel",
    acceptHint: "Accepted formats: JPEG, PNG or WebP (rejection: COD. 6.10.15)",
    tooLarge: "File larger than 10 MB",
  },
  participants: {
    title: "Participants",
    signLead:
      "Participants registered in General data. Signatures are collected in this step. Finalize is only available here.",
    name: "Name",
    add: "Add",
    addTitle: "Add participant",
    remove: "Remove",
    signed: "Signed",
    pending: "No signature",
    pendingSignature: "Pending signature",
    sign: "Sign",
    resign: "Re-sign",
    roleResponsible: "Responsible",
    roleExtra: "Extra",
    kindGs3: "GS3",
    kindExternal: "External",
    finalizeHint: "Finalize is enabled when every participant has signed.",
  },
  signature: {
    titleWithName: "Sign — {{name}}",
    fonts: {
      classic: "Classic",
      elegant: "Elegant",
      casual: "Casual",
      bold: "Bold",
      modern: "Modern",
      rounded: "Rounded",
    },
    name: "Name on signature",
    font: "Choose a signature font",
    preview: "Live preview",
    confirm: "Confirm signature",
    signedAt: "Signed at",
  },
  finalize: {
    title: "Finalize inspection",
    subtitle:
      "Review the summary below. After confirming, the form becomes read-only and the PDF is generated.",
    warningBefore: "This action ",
    warningIrreversible: "cannot be undone",
    warningAfter: ". After finalizing, no form data can be changed.",
    confirm: "Confirm and finalize",
    confirming: "Finalizing…",
    cancel: "Cancel",
    code: "Code",
    inspectionDate: "Inspection date",
    signatures: "Signatures",
    signaturesOf: "{{signed}} of {{total}}",
    anomalyCount: "Anomaly count",
    ecSum: "EC sum",
  },
  delete: {
    title: "Permanently delete item",
    caption: "Item to be deleted:",
    description:
      "This action is irreversible. Data cannot be recovered after deletion.",
    confirm: "Delete",
    cancel: "Cancel",
    item: "Inspection #{{code}} — {{date}}",
  },
  catalog: {
    v1: inspectionCatalogV1EnUS,
  },
};
